import { BsXCircle } from '@react-icons/all-files/bs/BsXCircle';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { BsCheckCircle } from '@react-icons/all-files/bs/BsCheckCircle';
import { BsFillSquareFill } from '@react-icons/all-files/bs/BsFillSquareFill';
import { BsExclamationTriangle } from '@react-icons/all-files/bs/BsExclamationTriangle';
import { autoinject, inject } from '../Utils/AutoInject';
import { bind } from '../Utils/Bind';
import { Enums } from '../Utils/Enums';
import { isUserTask, SessionTask, TasksStore } from '../Utils/TasksStore';
import { BsChatFill } from '@react-icons/all-files/bs/BsChatFill';

export interface INotificationToastProps {
    readonly task: Mri.UserTasks.UserTask | SessionTask;
}

@autoinject
@observer
export class NotificationToast extends React.Component<INotificationToastProps> {
    public componentDidMount() {
        this.fadeHandle = window.setTimeout(
            action(() => (this.fade = true)),
            TasksStore.toastTimeout - 1000
        );
    }

    public componentWillUnmount() {
        window.clearTimeout(this.fadeHandle);
    }

    public render() {
        const { task } = this.props;
        const isWarning = (task as any).isWarning;
        const isNotification = (task as any).isNotification;
        const onClick = (task as any).onClick as (() => void) | undefined;

        return (
            <div
                className={this.stateClass + this.fadeClass + ' toast-item rounded mb-3 border'}
                style={onClick ? { cursor: 'pointer' } : undefined}
                onClick={onClick ? () => { onClick(); this.dismiss(); } : undefined}
            >
                <div className="row h-100">
                    <div className="col-2">
                        {isNotification && (
                            <div className="flex-items-center h-100">
                                <BsChatFill className="text-info f-20" />
                            </div>
                        )}
                        {task.state == Mri.UserTasks.UserTaskState.Completed && !isWarning && !isNotification && (
                            <div className="flex-items-center h-100">
                                <BsCheckCircle className="text-success f-20" />
                            </div>
                        )}
                        {isWarning && (
                            <div className="flex-items-center h-100">
                                <BsExclamationTriangle className="text-warning f-20" />
                            </div>
                        )}
                        {task.state == Mri.UserTasks.UserTaskState.Failed && !isWarning && (
                            <div className="flex-items-center h-100">
                                <BsXCircle className="text-danger f-20" />
                            </div>
                        )}
                        {task.state == Mri.UserTasks.UserTaskState.Running && (
                            <div className="flex-items-center h-100">
                                <span className="pe-2">
                                    <BsFillSquareFill className="connect-fade" />
                                    <BsFillSquareFill className="connect-fade" />
                                    <BsFillSquareFill className="connect-fade" />
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="col-9 d-flex">
                        <div className="w-100" style={isWarning || isNotification ? { whiteSpace: 'normal', wordBreak: 'break-word' } : {}}>
                            {task.name}

                            {!isWarning && !isNotification && !task.exception && (
                                <div className="w-100">
                                    <small className="fw-bold">{Enums.toString(Enums.TaskState, task.state)}</small>
                                </div>
                            )}

                            {task.exception && (
                                <a onClick={this.expand}>
                                    <div className={this.expanded ? 'w-100 ws-initial wb-all' : 'w-100 ellipsis'}>
                                        <small className="fw-bold">{this.errorMessage}</small>
                                    </div>
                                </a>
                            )}
                        </div>
                    </div>

                    <div className="col-1 p-0">
                        {task.state != Mri.UserTasks.UserTaskState.Running && (
                            <a className="flex-items-center h-100 text-center" onClick={this.dismissOnly}>
                                <BsXCircle />
                            </a>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    private expand() {
        this.expanded = !this.expanded;
    }

    @bind
    private dismiss() {
        const guid = isUserTask(this.props.task) ? this.props.task.userTaskGuid : this.props.task.sessionTaskGuid;
        this.tasksStore.dismissToast(guid);
    }

    @bind
    private dismissOnly(e: React.MouseEvent) {
        e.stopPropagation();
        this.dismiss();
    }

    @computed
    private get stateClass() {
        const isWarning = (this.props.task as any).isWarning;
        const isNotification = (this.props.task as any).isNotification;
        if (isWarning) return 'text-warning';
        if (isNotification) return 'text-info';
        return this.props.task.state == Mri.UserTasks.UserTaskState.Failed ? 'text-danger' : 'text-teal';
    }

    @computed
    private get fadeClass() {
        return this.fade ? ' fade-out' : '';
    }

    @computed
    private get errorMessage() {
        const { task } = this.props;

        if (task.state != Mri.UserTasks.UserTaskState.Failed) return;
        if (!task.exception) return;

        const message =
            typeof task.exception == 'object' ? task.exception.message ?? task.exception.Message : task.exception;

        return (
            <span>
                {Enums.toString(Enums.TaskState, task.state)} - {message}
            </span>
        );
    }

    @observable
    private expanded = false;

    private fadeHandle?: number;
    @observable
    private fade = false;
    @inject
    private readonly tasksStore!: TasksStore;
}
